import React, { useContext, useMemo } from 'react'
import ReactECharts from 'echarts-for-react';
import { format } from 'date-fns';
import { useMediaQuery } from '@mui/material';
import { ColorModeContext } from "../../theme/ColorModeContext";
import { chartAxisStyle, getChartTheme } from "./chartTheme";

const BMIChart = (Props: any) => {
  const {
    bmiList,
    bmiChartZoneData,
    isMobileScreen,
    isDownload
  } = Props;
  const { mode } = useContext(ColorModeContext);
  const chartTheme = useMemo(
    () => getChartTheme(mode, Boolean(isDownload)),
    [mode, isDownload],
  );

  // const bmiValues = bmiList
  //   ?.filter((x) => {
  //     const isMoreThanOneYear =
  //       x?.age?.includes("y") && parseInt(x?.age?.split("y")[0]) >= 5;

  //     return isMoreThanOneYear && x?.bmi && x?.bmi > 0; // <-- filter out 0
  //   })
  //   .map((item) => [
  //     new Date(item?.chartLabel).getTime(),
  //     item?.bmi ?? 0,
  //   ]);

  const {
    bmiValues,
    currentBMI,
    zoneCenterLine,
    yAxisMaxPoint,
    maxDate,
    lastZone,
    lastZoneTime,
  } = useMemo(() => {
    const bmiValues = bmiList
      ?.filter((item: any) => item?.bmi && item?.bmi > 0) // filter out 0
      ?.map((item: any) => [new Date(item?.chartLabel)?.getTime(), item?.bmi ?? 0]);

    const filteredBMI = bmiList
      ?.filter((item: any) => item?.bmi && item?.bmi > 0)
      ?.map((item: any) => [new Date(item?.chartLabel)?.getTime(), item?.bmi ?? 0]);

    const maxDate = Math.max(...filteredBMI.map((d: any) => d[0]));
    const lastZone = bmiChartZoneData[bmiChartZoneData.length - 1];
    const lastZoneTime = new Date(lastZone?.ageDate)?.getTime();

    const zoneCenterLine = bmiChartZoneData
      .filter((h: any) => new Date(h?.ageDate)?.getTime() <= maxDate)
      .map((h: any) => [new Date(h?.ageDate)?.getTime(), h.zoneCenterGreenValue]);

    if (lastZoneTime < maxDate) {
      zoneCenterLine.push([maxDate, lastZone.zoneCenterGreenValue]);
    }

    const maxBMIZoneValue =
      bmiChartZoneData?.reduce(
        (curr: number, item: any) => Math.max(curr, item.zoneUpperRedMaxValue ?? 0),
        0
      ) || 0;

    const maxBMI = filteredBMI?.reduce(
      (curr: number, item: any) => Math.max(curr, item[1] ?? 0),
      0
    ) || 0;

    const yAxisMaxPoint = Math.ceil(Math.max(maxBMIZoneValue, maxBMI) / 10) * 10;

    return { bmiValues, currentBMI: filteredBMI, zoneCenterLine, yAxisMaxPoint, maxDate, lastZone, lastZoneTime };
  }, [bmiList, bmiChartZoneData]);

  const hasData = currentBMI?.length > 0;

  if (!hasData) {
    return null; // hide chart if no data
  }

  // Convert zone data to polygon format
  const createZonePolygon = (
    name: string,
    minKey: string,
    maxKey: string,
    color: string
  ) => {
    const maxPoints = bmiChartZoneData.map((p: any) => [new Date(p?.ageDate).getTime(), p[maxKey]]);
    const minPoints = bmiChartZoneData.map((p: any) => [new Date(p?.ageDate).getTime(), p[minKey]]).reverse();

    if (lastZoneTime < maxDate) {
      maxPoints.push([maxDate, lastZone[maxKey]]);
      minPoints.unshift([maxDate, lastZone[minKey]]);
    }

    const polygonPoints = maxPoints.concat(minPoints);

    return {
      name,
      type: "line",
      data: polygonPoints,
      lineStyle: { width: 0 },
      symbol: "none",
      areaStyle: { color, opacity: 0.3 },
      smooth: true,
      tooltip: { show: false },
      z: 1,
      animation: false, // Disable animation
    };
  };

  const color = {
    red: "#FF0000",
    orange: "#FFA500",
    yellow: "#FFFF00",
    green: "#008000",
  };

  const BMIOption = {
    animation: false, //  Disable global animation
    animationDuration: 0,
    animationEasing: 'linear',
    animationThreshold: 0,
    progressive: 0,
    hoverLayerThreshold: 0,
    backgroundColor: "transparent",
    textStyle: { color: chartTheme.text },
    tooltip: {
      trigger: 'axis',
      backgroundColor: chartTheme.tooltipBg,
      borderColor: chartTheme.tooltipBorder,
      borderWidth: 1,
      textStyle: {
        color: chartTheme.tooltipText,
        fontSize: 12,
      },
      formatter: function (params: any) {
        const dotColors = {
          "Current BMI": chartTheme.currentLine,
        };

        const firstPoint = params[0].value[0];
        const seriesData = bmiList.find(
          (x: any) => new Date(x.chartLabel).getTime() === firstPoint
        );

        const age = seriesData ? seriesData.age : null;

        let html = `<div style="text-align:left;color:${chartTheme.tooltipText};">`;
        html += `<div style="font-weight:bold; margin-bottom:4px;">
          ${format(new Date(params[0].value[0]), "dd-MM-yyyy")}
          ${age ? `(${age})` : ""}
        </div>`;

        params.forEach((item: any) => {
          const color = dotColors[item.seriesName] || item.color;
          html += `
          <div style="margin-bottom:2px;">
            <span style="width:8px; height:8px; border-radius:50%; background-color:${color}; display:inline-block; margin-right:6px;"></span>
            <span>${item.seriesName}: <strong>${item.data[1]}</strong> kg/m²</span>
          </div>`;
        });

        html += `</div>`;
        return html;
      },
    },
    title: {
      text: 'BMI Chart',
      left: 'center',
      top: isMobileScreen ? 5 : 10, // small offset
      textStyle: {
        fontSize: isMobileScreen ? 13 : 18,
        fontWeight: 600,
        color: chartTheme.text,
      },
    },
    legend: {
      data: [
        { name: "97-99 Percentile", itemStyle: { color: "#FF0000" } },
        { name: "91-96 Percentile", itemStyle: { color: "#FFA500" } },
        { name: "76-90 Percentile", itemStyle: { color: "#FFFF00" } },
        { name: "26-75 Percentile", itemStyle: { color: "#008000" } },
        { name: "50 Percentile", itemStyle: { color: "#008000" } },
        { name: "26-75 Percentile", itemStyle: { color: "#008000" } },
        { name: "Current BMI", itemStyle: { color: chartTheme.currentLine } },
        { name: "11-25 Percentile", itemStyle: { color: "#FFFF00" } },
        { name: "4-10 Percentile", itemStyle: { color: "#FFA500" } },
        { name: "1-3 Percentile", itemStyle: { color: "#FF0000" } }
      ],
      // type: isDownload ? "plain" : "scroll",
      type: "plain",
      orient: isMobileScreen ? "horizontal" : "vertical",
      top: isMobileScreen ? 25 : "middle",     // ✅ move legend below title
      left: isMobileScreen ? "center" : "auto",
      right: isMobileScreen ? "auto" : 10,
      bottom: isMobileScreen ? "auto" : "auto",
      align: isMobileScreen ? "auto" : "left",
      itemWidth: 14,
      itemHeight: 10,
      textStyle: {
        fontSize: isMobileScreen ? 10 : 11,
        color: chartTheme.text,
      },
      pageTextStyle: { color: chartTheme.text },
      padding: isMobileScreen ? [5, 5, 5, 5] : 0, // ✅ more breathing room
    },
    grid: {
      top: isMobileScreen ? 130 : 50,
      right: isMobileScreen ? 0 : 130,
      left: isMobileScreen ? 60 : 100,
    },
    xAxis: {
      type: 'time',
      name: 'Age',
      nameLocation: 'middle',
      nameGap: 30,
      ...chartAxisStyle(chartTheme),
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: yAxisMaxPoint,
      name: 'BMI (kg/m²)',
      nameLocation: 'middle',
      nameRotate: 90,
      interval: Math.ceil(yAxisMaxPoint / 20),
      nameGap: 40,
      ...chartAxisStyle(chartTheme),
    },
    // dataZoom: [
    //   {
    //     type: 'inside',
    //     start: 0,
    //     end: 100,
    //   },
    //   {
    //     start: 0,
    //     end: 100,
    //   }
    // ],
    dataZoom: isDownload
      ? [] // hide sliders when downloading
      : [
        // Horizontal zoom (x-axis)
        {
          type: 'inside',
          xAxisIndex: 0,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          start: 0,
          end: 100,
          bottom: isMobileScreen ? 10 : 0, // ✅ slightly above bottom for mobile
          handleSize: 8,
          handleStyle: { borderWidth: 1 },
          borderWidth: 1,
          height: 12, // thinner horizontal slider
        },
        // Vertical zoom (y-axis)
        {
          type: 'inside',
          yAxisIndex: 0,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
        },
        // {
        //   type: 'slider',
        //   yAxisIndex: 0,
        //   start: 0,
        //   end: 100,
        //   left: isMobileScreen ? 40 : 80, // ✅ push closer on mobile
        //   orient: 'vertical',
        //   width: 8, // ✅ thinner vertical line
        //   handleSize: 8,
        //   handleStyle: { borderWidth: 1 },
        //   borderWidth: 1,
        // },
      ],
    series: [
      // Zones as polygons
      createZonePolygon(
        '1-3 Percentile',
        'zoneLowerRedMinValue',
        'zoneLowerRedMaxValue',
        color.red
      ),
      createZonePolygon(
        '4-10 Percentile',
        'zoneLowerOrangeMinValue',
        'zoneLowerOrangeMaxValue',
        color.orange
      ),
      createZonePolygon(
        '11-25 Percentile',
        'zoneLowerYellowMinValue',
        'zoneLowerYellowMaxValue',
        color.yellow
      ),
      createZonePolygon(
        '26-75 Percentile',
        'zoneLowerGreenMinValue',
        'zoneLowerGreenMaxValue',
        color.green
      ),
      createZonePolygon(
        '26-75 Percentile',
        'zoneUpperGreenMinValue',
        'zoneUpperGreenMaxValue',
        color.green
      ),
      createZonePolygon(
        '76-90 Percentile',
        'zoneUpperYellowMinValue',
        'zoneUpperYellowMaxValue',
        color.yellow
      ),
      createZonePolygon(
        '91-96 Percentile',
        'zoneUpperOrangeMinValue',
        'zoneUpperOrangeMaxValue',
        color.orange
      ),
      createZonePolygon(
        '97-99 Percentile',
        'zoneUpperRedMinValue',
        'zoneUpperRedMaxValue',
        color.red
      ),

      // Center green dashed line
      {
        name: '50 Percentile',
        type: 'line',
        // data: bmiChartZoneData.map(h => [
        //   new Date(h.ageDate).getTime(),
        //   h.zoneCenterGreenValue
        // ]),
        data: zoneCenterLine,
        lineStyle: { color: color.green, width: 1, type: 'solid' },
        showSymbol: false,
        tooltip: { show: false },
        z: 3,
        animation: false,//  Disable line animation
      },
      {
        name: 'Current BMI',
        type: 'line',
        data: bmiValues,
        lineStyle: {
          type: 'solid',
          width: 2,
          color: chartTheme.currentLine,
        },
        symbol: "circle",
        itemStyle: {
          color: chartTheme.currentLine,
        },
        symbolSize: 6,
        z: 3,
        smooth: true,
        animation: false,//  Disable line animation
      },
    ],
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <ReactECharts
        option={BMIOption}
        style={{
          width: "100%",
          height: isDownload ? "750px" : isMobileScreen ? "70vh" : "85vh"  // <-- mobile vs full screen
        }}
        notMerge={true}
        lazyUpdate={true}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  )
}

export default BMIChart
